import type { DocPerm, Edges, NodeId, Permissions, TagId, TagEdges } from './types';
import { tagDescendants } from './tags';

/**
 * Role permissions.
 *
 * Two independent gates decide whether a control is shown, and BOTH have to pass:
 * permissions (may THIS USER) and capabilities (does THIS DEPLOYMENT — see
 * `/api/capabilities` and DEPLOYMENTS.md). This module is only the first.
 *
 * The client gates are for UX. The server enforces the same rules independently in
 * `server/src/auth/`, and that is what actually protects anything: since V0.43 a hidden branch
 * really is filtered out of `GET /api/map` before it reaches the browser, and the doc, search,
 * chat, feed and health endpoints are scoped by the same rule (`auth/visibilityCore.ts`).
 *
 * This paragraph claimed all of that for several releases while it was false — the map payload
 * carried every node and only the renderer skipped them, so anybody could read a hidden branch
 * out of the Network tab. Whatever this file says about the server, check `PERMISSIONS.md`
 * against the route before believing it.
 *
 * WHAT IS STILL NOT TRUE: hiding is not confidentiality against an EDITOR. `EDIT_MAP` can
 * reparent a node out of a hidden branch and `ASSIGN_TAGS` can remove the tag that hides it.
 * Both are legitimate uses of a granted brick, and no gate here or on the server pretends
 * otherwise.
 */

export const ROLES = {
  /** The LEGACY full-editor macro. Stored roles keep it; /api/auth/me expands it (see below). */
  edit: 'SHOW_EDIT',
  userPermissions: 'SHOW_USER_PERMISSIONS',
  aiAdmin: 'AI_ADMIN',
  chat: 'USE_CHAT',
  presence: 'SHOW_PRESENCE',
  // The seven permission BRICKS (V0.37). `perm.roles` arrives EXPANDED from /api/auth/me —
  // a role whose STORED roles hold only SHOW_EDIT reaches this module already carrying all
  // seven, so nothing client-side ever re-derives what the macro means (plan trap #5:
  // `server/src/auth/roles.ts` `expandRoles()` is the ONE expansion). V0.37.1: the same
  // expanded shape now folds in whatever a user's GROUP grants too — merged server-side in
  // `resolvePermissions()`, so this module still sees one flat, already-merged `perm.roles`.
  editMap: 'EDIT_MAP',
  editTags: 'EDIT_TAGS',
  assignTags: 'ASSIGN_TAGS',
  editLinks: 'EDIT_LINKS',
  nodeResponsible: 'NODE_RESPONSIBLE',
  docResponsible: 'DOC_RESPONSIBLE',
  editAllDocs: 'EDIT_ALL_DOCS',
  /**
   * Change the web address of a node or a document (V0.45.1). STANDALONE — deliberately NOT a
   * brick and NOT implied by `SHOW_EDIT`, so `brickOrMacro` is the wrong gate for it and
   * `canReslug` below uses a bare `hasRole`. See server/src/auth/roles.ts for why: this is the
   * one permission whose consequences land on people who are not in the app at all, namely
   * anybody holding a link, so it is granted on purpose or not at all.
   */
  reslug: 'RESLUG',
  /** Read the action log — every act by every person and by the AI (V0.49). */
  showLogs: 'SHOW_LOGS',
  /**
   * Export, download, upload and RESTORE a backup of the whole installation (V0.48). Standalone
   * for RESLUG's reason carried further: a restore replaces every account, role, document and
   * revision at once, and it is deliberately not implied by `SHOW_USER_PERMISSIONS` either —
   * whoever holds that could grant themselves this, but granting it in a screen that records
   * the change is not the same as having it already, one click away.
   */
  backupAdmin: 'BACKUP_ADMIN',
  /**
   * See the to-do section on a node and the My to-dos section in the pane (V0.50) — and tick
   * a to-do assigned to YOU, which needs no second permission because assigning somebody work
   * and then gating their ability to report it finished is not a policy anybody wants.
   */
  viewTodos: 'VIEW_TODOS',
  /** Create, edit, reassign, re-order and delete to-dos. Deliberately does not imply the above. */
  editTodos: 'EDIT_TODOS',
} as const;

/** The seven bricks, as the server's `ALL_BRICKS` lists them. */
export const BRICK_ROLES = [
  ROLES.editMap,
  ROLES.editTags,
  ROLES.assignTags,
  ROLES.editLinks,
  ROLES.nodeResponsible,
  ROLES.docResponsible,
  ROLES.editAllDocs,
] as const;

/**
 * A PARTICIPANT in the documentation pipeline — the mirror of the server's
 * `PARTICIPANT_ROLES` (auth/roles.ts): any brick, or the AI operator. This is who the server
 * ships per-node `responsibles` to, who may read the user directory and the reviews map, and
 * therefore who edit MODE is offered to.
 */
export const PARTICIPANT_ROLES = [...BRICK_ROLES, ROLES.aiAdmin] as const;

export function hasRole(perm: Permissions | null | undefined, role: string): boolean {
  return !!perm?.roles?.includes(role);
}

/**
 * The legacy SHOW_EDIT check, kept for exactly one caller class: the frozen core-parity
 * oracle (`e2e/15-core-parity.spec.ts`) imports it and replays a recording made when this WAS
 * the edit gate. Live code wants `canEnterEdit` (the edit-MODE gate) or one of the per-brick
 * helpers below — every app call site was retargeted in V0.37, and a new one reaching for
 * this name is almost certainly asking the wrong question.
 */
export const canEdit = (perm: Permissions | null | undefined) => hasRole(perm, ROLES.edit);
export const canUserPerms = (perm: Permissions | null | undefined) => hasRole(perm, ROLES.userPermissions);
export const canAiAdmin = (perm: Permissions | null | undefined) => hasRole(perm, ROLES.aiAdmin);
export const canChat = (perm: Permissions | null | undefined) => hasRole(perm, ROLES.chat);
export const canPresence = (perm: Permissions | null | undefined) => hasRole(perm, ROLES.presence);
/** A BARE role check, not `brickOrMacro`: the full-editor macro does not imply this one. */
export const canReslug = (perm: Permissions | null | undefined) => hasRole(perm, ROLES.reslug);

/**
 * May this person read the ACTION LOG (V0.49)?
 *
 * Bare `hasRole`, like `canReslug` and unlike the brick gates: SHOW_LOGS is standalone and
 * `SHOW_EDIT` deliberately does not imply it. It grants a view of colleagues rather than power
 * over documents, so it is ticked on purpose or it is not held.
 */
export const canViewLogs = (perm: Permissions | null | undefined): boolean => hasRole(perm, ROLES.showLogs);
/** Bare too, and for the stronger version of the same reason. See `ROLES.backupAdmin`. */
export const canBackupAdmin = (perm: Permissions | null | undefined) => hasRole(perm, ROLES.backupAdmin);

/**
 * The to-do gates (V0.50). BARE `hasRole` like the three standalones above, not `brickOrMacro`:
 * `SHOW_EDIT` deliberately does not imply either, because to-dos are a different JOB from
 * documentation rather than a smaller slice of it — see `server/src/auth/roles.ts`.
 *
 * `canEditTodos` requires BOTH, which mirrors the server's `requireAllRoles(VIEW, EDIT)` gate
 * exactly. A role holding only the write permission sees nothing and writes nothing, and that
 * is the honest reading of the combination rather than a silent widening — so the UI must not
 * quietly draw the controls that the server would then refuse.
 */
export const canViewTodos = (perm: Permissions | null | undefined) => hasRole(perm, ROLES.viewTodos);
export const canEditTodos = (perm: Permissions | null | undefined) =>
  hasRole(perm, ROLES.viewTodos) && hasRole(perm, ROLES.editTodos);

/* ---- the V0.37 gates ---------------------------------------------------------------- */

/**
 * Every brick helper below is BRICK-OR-MACRO, deliberately. `/api/auth/me` expands the macro
 * server-side, so against a current server the macro clause never decides anything — but a
 * roles array carrying `SHOW_EDIT` without its bricks is reachable (a server one release
 * older than this client; the regression suite's own idiom of assigning to `perm.roles`), and
 * a legacy full editor must never lose the editor to a version skew. This is read-side
 * TOLERANCE of the fact the server's `expandRoles()` states, not a second expansion: nothing
 * here rewrites a stored array, which is trap #5's actual failure mode.
 */
const brickOrMacro = (perm: Permissions | null | undefined, brick: string): boolean =>
  hasRole(perm, brick) || hasRole(perm, ROLES.edit);

/**
 * May this user ENTER EDIT MODE — any participant brick, or the AI operator (who must be able
 * to reach the AI console, an edit-mode surface, with no brick of their own). Replaces
 * `canEdit()` as the mode gate: since V0.37 "may edit" is not one question, and what edit mode
 * itself needs is only "is there anything in it for this user". Each affordance inside the
 * mode carries its own precise gate below.
 */
export const canEnterEdit = (perm: Permissions | null | undefined): boolean =>
  hasRole(perm, ROLES.edit) || PARTICIPANT_ROLES.some((r) => hasRole(perm, r));

/** Node structure: create/move/delete nodes, doc create/delete, slot wiring, renames. */
export const canEditMap = (perm: Permissions | null | undefined) => brickOrMacro(perm, ROLES.editMap);
/** Tag structure: create/edit/delete tags and categories — and the tag tree's edit shape. */
export const canEditTags = (perm: Permissions | null | undefined) => brickOrMacro(perm, ROLES.editTags);
/** Node ↔ tag assignment (the popup's chips, and the inheritance stop-blocks). */
export const canAssignTags = (perm: Permissions | null | undefined) => brickOrMacro(perm, ROLES.assignTags);
/** Dotted cross-links between nodes. */
export const canEditLinks = (perm: Permissions | null | undefined) => brickOrMacro(perm, ROLES.editLinks);
/** The composable admin override: edit any document, prompt and workflow, assign anyone. */
export const canEditAllDocs = (perm: Permissions | null | undefined) => brickOrMacro(perm, ROLES.editAllDocs);

/**
 * Is this documentation slot offered to the user?
 *
 * Defaults to TRUE when there is no payload, matching the vanilla behaviour. That is the
 * right default for a UX gate — an unknown permission set should not blank the app — and it
 * is safe only because the server independently refuses what it should not serve.
 */
export function canDocBtn(perm: Permissions | null | undefined, kind: DocPerm): boolean {
  if (!perm?.docButtons) return true;
  return perm.docButtons[kind] !== false;
}

/**
 * The resolved hidden sets, expanded once from the raw payload.
 *
 * Hidden TAGS expand to their descendants: hiding a parent tag has to hide the sub-tags
 * under it, or the branch is still reachable through a child. Hidden NODES do not expand
 * here — `isPermHidden` walks ancestors instead, which is the same answer without
 * materialising every descendant of a hidden branch.
 */
export interface HiddenSets {
  nodes: ReadonlySet<NodeId>;
  tags: ReadonlySet<TagId>;
}

export function buildHiddenSets(
  perm: Permissions | null | undefined,
  tagEdges: TagEdges,
): HiddenSets {
  const nodes = new Set<NodeId>(perm?.hiddenNodes ?? []);
  const tags = new Set<TagId>();
  for (const t of perm?.hiddenTags ?? []) {
    tags.add(t);
    for (const d of tagDescendants(tagEdges, t)) tags.add(d);
  }
  return { nodes, tags };
}

export const EMPTY_HIDDEN: HiddenSets = { nodes: new Set(), tags: new Set() };

/**
 * Is a node hidden by permissions — itself, or anywhere under a hidden ancestor?
 *
 * The ancestor walk is the point: `hiddenNodes` lists BRANCH ROOTS, not every node in the
 * branch. Checking membership alone would leak every child of a hidden node.
 */
export function isPermHidden(id: NodeId, edges: Edges, hidden: HiddenSets): boolean {
  if (!hidden.nodes.size) return false;
  let cur: NodeId | undefined = id;
  let guard = 0;
  while (cur !== undefined && guard++ < 10_000) {
    if (hidden.nodes.has(cur)) return true;
    cur = edges[cur];
  }
  return false;
}

export function isTagHidden(id: TagId, hidden: HiddenSets): boolean {
  return hidden.tags.has(id);
}
